import { useRouter } from "vue-router";
import { EligibilityCheckReminderContent } from "./eligibilityCheckReminderContent";
import { ProceedingContent } from "./ProceedingContent";
import { AppStateChangedContent } from "./appStateChangedContent";
import { NoteAddedContent } from "./noteAddedContent";
import { AppReceivedContent } from "./appReceivedContent";
import { useNotificationStore } from "src/stores/notifications-store";
import { DealerDocsOutContent } from "./dealerDocsOutContent";
import { PayoutContent } from "./payoutContent";
import { DealtContent } from "./dealtContent";

interface INotificationReceived {
    id: string;
    userId: string;
    scenario: string;
    content: string;
    isDelivered: boolean;
    deliveredAt: Date;
    created: Date;
    readBy: string[];
}

class NotificationReceived {
    id: string;
    userId: string;
    scenario: string;
    content: string;
    isDelivered: boolean;
    deliveredAt: Date;
    created: Date;
    readBy: string[];

    constructor(notification: INotificationReceived) {
        this.id = notification.id;
        this.userId = notification.userId;
        this.scenario = notification.scenario;
        this.content = notification.content;
        this.isDelivered = notification.isDelivered;
        this.deliveredAt = notification.deliveredAt;
        this.readBy = notification.readBy;
        this.created = notification.created;
    }

    public get contentObject(): 
    EligibilityCheckReminderContent | AppStateChangedContent | ProceedingContent | NoteAddedContent | 
    AppReceivedContent | DealerDocsOutContent | PayoutContent | DealtContent | null {
        switch (this.scenario) {
            case "EligibilityCheckReminder":
                return JSON.parse(this.content) as EligibilityCheckReminderContent;
            case "ApplicationStateChanged":
            case "UnableToContact":
            case "NotProceeding":
                return JSON.parse(this.content) as AppStateChangedContent;
            case "Proceeding":
                return JSON.parse(this.content) as ProceedingContent;
            case "NoteCreated":
                return JSON.parse(this.content) as NoteAddedContent;
            case "ApplicationReceived":
                return JSON.parse(this.content) as AppReceivedContent;
            case "DealerDocsOut":
                return JSON.parse(this.content) as DealerDocsOutContent;
            case "Payout":
            case "DealerValCall":
                return JSON.parse(this.content) as PayoutContent;
            case "Dealt":
                return JSON.parse(this.content) as DealtContent;
            default:
                return null;
        }   
    }

    public get title(): string {
        switch (this.scenario) {
            case "EligibilityCheckReminder":
                return "Eligibility Check Reminder";
            case "ApplicationStateChanged":
                return "Application State Changed";
            case "UnableToContact":
                return "Unable to Contact";
            case "NotProceeding":
                return "Not Proceeding";
            case "Proceeding":
                return "Proceeding";
            case "NoteCreated":
                return "Note Added";
            case "ApplicationReceived":
                const a = (this.contentObject as AppReceivedContent);
                return `${a.appType}`;
            case "DealerDocsOut":
                return "Documents Requested";
            case "Payout":
                return "Deal Sent To Lender";
            case "DealerValCall":
                return "Deal Paid Out";
            case "Dealt":
                return "Deal Completed";
            default:
                return "Notification";
        }
    }

    public get body(): string {
        switch (this.scenario) {
            case "EligibilityCheckReminder":
                const e = (this.contentObject as EligibilityCheckReminderContent);
                return `You have ${e.applications.length} eligibility ${e.applications.length > 1 ? "checks" : "check"} yet to be converted to full proposal`;
            case "ApplicationStateChanged":
                const o = (this.contentObject as AppStateChangedContent);
                return  `${o.customerName} (${o.applicationId}) <br/> ${o.previousState ?  ("From "+o.previousState+" to") : "to"} <b>${o.applicationState}</b>` ;
            case "UnableToContact":
                const u = (this.contentObject as AppStateChangedContent);
                return `${u.customerName} (${u.applicationId}) <br/> We are unable to contact this customer, you may want to try reach out to them`;
            case "NotProceeding":
                const np = (this.contentObject as AppStateChangedContent);
                return `${np.customerName} (${np.applicationId}) <br/> This application is no longer proceeding${np.applicationState !== null && np.applicationState !== "" ? "<b>"+np.applicationState+"</b>" : "" }.`;
            case "Proceeding":
                const p = (this.contentObject as ProceedingContent);
                return `${p.customerName} (${p.applicationId}) is proceeding on a quote with ${p.lenderName}`;
            case "NoteCreated":
                const n = (this.contentObject as NoteAddedContent);
                return `${n.customerName} (${n.applicationId}) <br/> ${n.agentName} (Car Finance 247) added a note`;
            case "ApplicationReceived":
                const a = (this.contentObject as AppReceivedContent);
                return `${a.customerName} (${a.applicationId}) ${a.vehicleReg ? "<br/>VRM: " + a.vehicleReg : ""}`;
            case "DealerDocsOut":
                const d = (this.contentObject as DealerDocsOutContent);
                return `${d.customerName} (${d.applicationId}) <br/> We require some documents from you, please check the documents tab of this application.`;
            case "Payout":
                const pay = (this.contentObject as PayoutContent);
                return `${pay.customerName} (${pay.applicationId}) <br/> Deal sent to <b>${pay.lenderName}</b> for payout, we will be in touch if we need anything else from you.`;
            case "DealerValCall":
                const dv = (this.contentObject as PayoutContent);
                return `${dv.customerName} (${dv.applicationId}) <br/> The lender has now paid out, we will be in touch to complete the validation calls before transferring funds`;
            case "Dealt":
                const dealt = (this.contentObject as DealtContent);
                return `${dealt.customerName} (${dealt.applicationId}) <br/> This deal has been completed and the <b>funds transferred to your bank account</b>. Thank you for using Car Finance 247.`;
            default:
                return this.content;
        }
    }

    clicked(){
        switch (this.scenario) {
            case "EligibilityCheckReminder":
                break;
            case "ApplicationStateChanged":
            case "UnableToContact":
            case "NotProceeding":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as AppStateChangedContent).applicationId}`);
                }
            case "Proceeding":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as ProceedingContent).applicationId}`);
                }
            case "NoteCreated":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as NoteAddedContent).applicationId}`);
                }
            case "ApplicationReceived":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as AppReceivedContent).applicationId}`);
                }
            case "DealerDocsOut":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as DealerDocsOutContent).applicationId}`);
                }
            case "Payout":
            case "DealerValCall":
                return (router: ReturnType<typeof useRouter>) => {
                    router.push(`/customer/${(this.contentObject as PayoutContent).applicationId}`);
                }
            default:
                break;
        }
    }
}

export type { INotificationReceived };
export { NotificationReceived };